// src/services/batchScreeningService.js
import { 
    collection, 
    addDoc, 
    doc, 
    getDoc, 
    getDocs, 
    onSnapshot, 
    query, 
    where, 
    orderBy, 
    limit,
    Timestamp
  } from 'firebase/firestore';
  import { db, functions } from '../firebase';
  import { httpsCallable } from 'firebase/functions';
  
  /**
   * Submit a new batch screening job
   * @param {Array<string>} names - Array of names to screen
   * @param {string} userId - Current user ID
   * @param {string} orgId - Organization ID
   * @returns {Promise<string>} - Job ID
   */
  export const submitBatchScreeningJob = async (names, userId, orgId) => {
    try {
      if (!names || names.length === 0) {
        throw new Error('No names provided for batch screening');
      }
      
      if (!orgId) {
        throw new Error('Organization ID is required for batch screening');
      }
      
      // Call the createBatchJob firebase function
      const createBatchJobFn = httpsCallable(functions, 'createBatchJob');
      const response = await createBatchJobFn({ names, userId, orgId });
      
      // Return the job ID from the response
      return response.data.jobId;
    } catch (error) {
      console.error('Error submitting batch job:', error);
      throw error;
    }
  };
  
  /**
   * Get job status and details
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} - Job data
   */
  export const getJobStatus = async (jobId) => {
    try {
      const jobRef = doc(db, 'batch-jobs', jobId);
      const jobSnapshot = await getDoc(jobRef);
      
      if (!jobSnapshot.exists()) {
        throw new Error('Job not found');
      }
      
      const jobData = jobSnapshot.data();
      
      // If this is a parent job, fetch child job details
      if (jobData.isParentJob) {
        // Fetch basic information about child jobs
        const childJobs = await Promise.all(
          (jobData.childJobs || []).map(async (childJobId) => {
            const childJobRef = doc(db, 'batch-jobs', childJobId);
            const childJobSnapshot = await getDoc(childJobRef);
            
            if (childJobSnapshot.exists()) {
              return {
                id: childJobId,
                ...childJobSnapshot.data()
              };
            }
            return null;
          })
        );
        
        // Filter out null values and return the job with child details
        return {
          id: jobId,
          ...jobData,
          childJobDetails: childJobs.filter(Boolean)
        };
      }
      
      // Return the regular job data
      return {
        id: jobId,
        ...jobData
      };
    } catch (error) {
      console.error('Error getting job status:', error);
      throw error;
    }
  };
  
  /**
   * Subscribe to real-time job updates
   * @param {string} jobId - Job ID
   * @param {Function} callback - Callback function with job data
   * @returns {Function} - Unsubscribe function
   */
  export const subscribeToJobUpdates = (jobId, callback) => {
    try {
      // Create a reference to the job document
      const jobRef = doc(db, 'batch-jobs', jobId);
      
      // Set up real-time listener
      const unsubscribe = onSnapshot(
        jobRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const jobData = {
              id: jobId,
              ...snapshot.data()
            };
            
            // Handle parent jobs - set up a combined listener for all child jobs
            if (jobData.isParentJob && jobData.childJobs && jobData.childJobs.length > 0) {
              // We'll handle child job updates in a separate listener to avoid callback loop
              setupChildJobsListener(jobId, jobData.childJobs, updatedJobData => {
                // Pass merged parent+child data to callback
                callback({
                  ...jobData,
                  childJobDetails: updatedJobData
                });
              });
            }
            
            callback(jobData);
          } else {
            callback(null, { code: 'not-found', message: 'Job not found' });
          }
        },
        (error) => {
          callback(null, error);
        }
      );
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up job subscription:', error);
      callback(null, error);
      // Return a no-op function as fallback
      return () => {};
    }
  };
  
  // Helper function to set up listeners for child jobs
  const setupChildJobsListener = (parentJobId, childJobIds, callback) => {
    if (!childJobIds || childJobIds.length === 0) return () => {};
    
    // Group jobs in batches of 10 for firestore limitations
    const jobBatches = [];
    for (let i = 0; i < childJobIds.length; i += 10) {
      jobBatches.push(childJobIds.slice(i, i + 10));
    }
    
    // Create listeners for each batch
    const unsubscribes = jobBatches.map(batch => {
      const q = query(
        collection(db, 'batch-jobs'),
        where('__name__', 'in', batch)
      );
      
      return onSnapshot(
        q,
        (snapshot) => {
          const jobsData = [];
          snapshot.forEach(doc => {
            jobsData.push({
              id: doc.id,
              ...doc.data()
            });
          });
          
          // Call the callback with this batch of data
          callback(jobsData);
        },
        (error) => {
          console.error('Error in child jobs listener:', error);
        }
      );
    });
    
    // Return a function that unsubscribes all listeners
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  };
  
  /**
   * Get results for a job
   * @param {string} jobId - Job ID
   * @returns {Promise<Array>} - Array of results
   */
  export const getJobResults = async (jobId) => {
    try {
      // First get the job document
      const jobDoc = await getJobStatus(jobId);
      
      if (!jobDoc) {
        throw new Error('Job not found');
      }
      
      // If this is a parent job, get results from all child jobs
      if (jobDoc.isParentJob) {
        const allResults = [];
        
        // Fetch results for each child job
        for (const childJobId of (jobDoc.childJobs || [])) {
          try {
            const childResults = await getChildJobResults(childJobId);
            allResults.push(...childResults);
          } catch (error) {
            console.error(`Error getting results for child job ${childJobId}:`, error);
          }
        }
        
        return allResults;
      }
      
      // For a regular job, get results directly
      return await getChildJobResults(jobId);
    } catch (error) {
      console.error('Error getting job results:', error);
      throw error;
    }
  };
  
  // Helper function to get results for a single job
const getChildJobResults = async (jobId) => {
  try {
    // First get the job to get the orgId
    const jobDoc = await getJobStatus(jobId);
    if (!jobDoc || !jobDoc.orgId) {
      console.error('Cannot get job results: job not found or missing orgId');
      return [];
    }
    
    // Query by organization first (as required by Firebase rules), then filter by batchJobId
    const searchesQuery = query(
      collection(db, 'sanction-searches'),
      where('orgId', '==', jobDoc.orgId),
      where('source', '==', 'mass-upload')
    );
    
    const searchesSnapshot = await getDocs(searchesQuery);
    
    // Filter to only searches for this specific batch job
    const jobSearchDocs = searchesSnapshot.docs.filter(doc => 
      doc.data().batchJobId === jobId
    );
      
          // If no searches found, check the job document for results
    if (jobSearchDocs.length === 0) {
        const jobRef = doc(db, 'batch-jobs', jobId);
        const jobSnapshot = await getDoc(jobRef);
        
        if (!jobSnapshot.exists()) {
          return [];
        }
        
        const jobData = jobSnapshot.data();
        
        // Return results from job document if available
        if (jobData.completedNames && jobData.verificationIds) {
          return jobData.completedNames.map((name, index) => ({
            name,
            status: 'completed',
            matches: 0,
            verificationId: jobData.verificationIds[index] || null
          }));
        }
        
        return [];
      }
      
          // Process searches into results
    const results = [];
    jobSearchDocs.forEach(doc => {
      const search = doc.data();
        
        results.push({
          name: search.searchTerm,
          status: search.matchesFound > 0 ? 'completed-with-matches' : 'completed',
          matches: search.matchesFound || 0,
          highConfidenceMatches: search.highConfidenceMatches || 0,
          verificationId: search.verificationId,
          searchId: doc.id,
          details: search.results || []
        });
      });
      
      return results;
    } catch (error) {
      console.error('Error getting child job results:', error);
      throw error;
    }
  };
  
  /**
   * Get organization's recent batch screening jobs
   * @param {string} orgId - Organization ID
   * @param {number} maxJobs - Maximum number of jobs to fetch (default: 50)
   * @returns {Promise<Array>} - Array of job objects
   */
  export const getUserJobs = async (orgId, maxJobs = 50) => {
    try {
      // Query organization jobs instead of user jobs
      const allJobsQuery = query(
        collection(db, 'batch-jobs'),
        where('orgId', '==', orgId),
        orderBy('startTime', 'desc'),
        limit(maxJobs)
      );
      
      const jobsSnapshot = await getDocs(allJobsQuery);
      
      const jobs = [];
      jobsSnapshot.forEach(doc => {
        jobs.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`Found ${jobs.length} jobs for organization ${orgId}`);
      
      return jobs;
    } catch (error) {
      console.error('Error getting organization jobs:', error);
      throw error;
    }
  };
  
  /**
   * Get a chain of continuation jobs
   * @param {string} originalJobId - Original job ID
   * @returns {Promise<Array>} - Array of related job IDs
   */
  export const getJobChain = async (originalJobId) => {
    try {
      // First get the job document to check if it's a root job or part of a chain
      const origJobDoc = await getDoc(doc(db, 'batch-jobs', originalJobId));
      
      if (!origJobDoc.exists()) {
        throw new Error('Job not found');
      }
      
      const origJobData = origJobDoc.data();
      
      // Determine the root job ID
      const rootJobId = origJobData.rootJobId || originalJobId;
      
      // Query all jobs in the chain using the rootJobId field
      const chainQuery = query(
        collection(db, 'batch-jobs'),
        where('rootJobId', '==', rootJobId)
      );
      
      // Get all jobs in the chain
      const chainSnapshot = await getDocs(chainQuery);
      const chainJobs = [];
      
      // Process the root job first (it may not have rootJobId field)
      if (rootJobId === originalJobId) {
        chainJobs.push({
          id: originalJobId,
          ...origJobData,
          continuationSeq: 0 // Ensure root job has sequence 0 if not set
        });
      }
      
      // Add all other jobs from the chain
      chainSnapshot.forEach(doc => {
        const jobData = doc.data();
        // Only add if not already added (avoid duplication of root job)
        if (doc.id !== originalJobId) {
          chainJobs.push({
            id: doc.id,
            ...jobData
          });
        }
      });
      
      // Sort jobs by continuationSeq to ensure correct order
      chainJobs.sort((a, b) => (a.continuationSeq || 0) - (b.continuationSeq || 0));
      
      // If we didn't find any jobs or just the original, try legacy chainId approach
      if (chainJobs.length <= 1) {
        // Legacy approach: follow the chain of continuation jobs
        const legacyChain = await getLegacyJobChain(originalJobId);
        if (legacyChain.length > 1) {
          return legacyChain;
        }
      }
      
      return chainJobs;
    } catch (error) {
      console.error('Error getting job chain:', error);
      throw error;
    }
  };
  
  /**
   * Legacy method to get a chain of continuation jobs by following continuationJobId
   * @param {string} originalJobId - Original job ID
   * @returns {Promise<Array>} - Array of related job IDs
   */
  const getLegacyJobChain = async (originalJobId) => {
    try {
      const jobs = [];
      let currentJobId = originalJobId;
      let nextJobExists = true;
      
      // Follow the chain of continuation jobs
      while (nextJobExists) {
        const jobDoc = await getDoc(doc(db, 'batch-jobs', currentJobId));
        
        if (!jobDoc.exists()) {
          nextJobExists = false;
          continue;
        }
        
        const jobData = jobDoc.data();
        jobs.push({
          id: currentJobId,
          ...jobData
        });
        
        // Check if this job has a continuation
        if (jobData.continuationJobId) {
          currentJobId = jobData.continuationJobId;
        } else {
          nextJobExists = false;
        }
      }
      
      return jobs;
    } catch (error) {
      console.error('Error getting legacy job chain:', error);
      throw error;
    }
  };